import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

/**
 * Typed PostHog events for the guest app.
 *
 * The React Native app has carried the same shape since launch
 * (`src/lib/analytics/events.ts` there): a map of event name to property shape,
 * and a thin `capture` that is the only way events are sent. Web deliberately
 * mirrors it rather than inventing a parallel vocabulary, because both clients
 * report into the *same* PostHog project — a web-only `roll_joined` alongside
 * RN's `guest_session_started` would split every funnel in two and quietly
 * under-count whichever platform the analyst forgot.
 *
 * So: the names below that RN already fires are copied verbatim, property keys
 * included (`rollGuid` vs `rollId` inconsistencies are RN's, and are preserved
 * on purpose — matching a wrong-but-established key beats being right alone).
 * Events marked "web-only" have no RN counterpart; they cover surfaces mobile
 * does not have, and are named in the same flat snake_case so they read as part
 * of one taxonomy.
 *
 * Everything is optional-chained through `posthog.__loaded`: the provider skips
 * `init` in development and when the key is absent, and capturing into an
 * uninitialised client throws. A no-op in dev is the intended behaviour, not a
 * degradation.
 */

type AuthMethod = "apple" | "google";

export type EventProperties = {
  // ── Auth ────────────────────────────────────────────────────────────────
  user_signed_in: { method: AuthMethod };
  user_signed_up: { method: AuthMethod };
  user_signed_out: undefined;
  auth_failed: {
    method: AuthMethod;
    reason: string;
    errorMessage?: string;
  };
  /**
   * Fired once the claim has run, whatever its verdict — RN does the same, so
   * the conversion count stays comparable. `rollsClaimed < rollsTotal` is how a
   * partial claim is spotted; `guest_claim_failed` covers the call itself dying.
   */
  guest_converted_to_user: {
    method: AuthMethod;
    rollsClaimed: number;
    rollsTotal: number;
    rollsFailed: number;
  };
  guest_claim_failed: { reason: string; rollsTotal: number };

  // ── Guest / invite ──────────────────────────────────────────────────────
  guest_session_started: { rollGuid?: string };
  guest_join_failed: { reason: string; rollGuid?: string };
  invite_accepted: { rollGuid?: string };
  invite_sent: { rollGuid?: string; method?: "link" | "qr" | "clipboard" };

  // ── Guest funnel (App Clip) ─────────────────────────────────────────────
  // Fired by the clip today; named here so the three surfaces stay one schema.
  invite_preview_viewed: { roll_guid?: string };
  invite_blocked: { variant: string; roll_guid?: string };
  qr_scanner_opened: undefined;
  auth_screen_viewed: undefined;
  auth_screen_dismissed: undefined;
  roll_complete_viewed: { photo_count?: number; is_guest?: boolean };
  app_store_overlay_shown: { source: string };

  // ── Camera ──────────────────────────────────────────────────────────────
  photo_captured: { rollGuid: string; photoIndex: number };
  capture_photo_failed: { reason: string; rollGuid?: string };
  camera_init_failed: { reason: string };
  camera_permission_denied: { permanentlyDenied?: boolean };

  // ── Upload ──────────────────────────────────────────────────────────────
  photo_upload_completed: { rollId: string; retryCount: number };
  photo_upload_rejected: { rollId: string; reason: string; refunded: boolean };
  upload_failed: {
    rollId: string;
    reason: string;
    retryCount: number;
    abandoned: boolean;
  };

  // ── Roll / album ────────────────────────────────────────────────────────
  roll_developed: { rollGuid: string };
  roll_reveal_viewed: { rollGuid: string };
  /** web-only: a photo saved to the visitor's device from the viewer. */
  photo_downloaded: { rollGuid?: string };
  /**
   * web-only: `method` records which branch of the Web Share ladder ran, since
   * a clipboard copy is a materially weaker share than a native file share.
   */
  photo_shared: { rollGuid?: string; method: "file" | "url" | "clipboard" };

  // ── Errors ──────────────────────────────────────────────────────────────
  api_error: {
    endpoint?: string;
    statusCode?: number;
    message: string;
    code?: string;
    // The Worker logs every 5xx under this id, so a Sentry event can be
    // joined to the log line — and to the Worker's own Sentry issue.
    requestId?: string;
  };
};

// The ids one searches Sentry by go in as tags; tags are indexed, `extra` is
// not. A request id as an extra can only be found by opening the event.
const TAGGED = ["requestId", "statusCode", "endpoint", "code"] as const;

function splitTags(properties: Record<string, unknown> | undefined) {
  const tags: Record<string, string | number | boolean> = {};
  const extra: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties ?? {})) {
    const taggable =
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean";
    if ((TAGGED as readonly string[]).includes(key) && taggable) tags[key] = value;
    else extra[key] = value;
  }
  return { tags, extra };
}

export type AnalyticsEvent = keyof EventProperties;

/**
 * Every capture in the app goes through here, so the event name and its
 * properties are checked against the map above at compile time. Call sites read
 * `analytics.capture("photo_captured", { rollGuid, photoIndex })`.
 */
export const analytics = {
  capture<E extends AnalyticsEvent>(event: E, properties?: EventProperties[E]) {
    // `__loaded` is false during SSR, in development, and whenever the key is
    // missing — all three are cases where dropping the event is correct.
    if (typeof window === "undefined" || !posthog.__loaded) return;
    posthog.capture(event, properties ?? undefined);
  },

  /** The event for PostHog and the exception for Sentry, in one call. */
  reportError<E extends AnalyticsEvent>(
    event: E,
    error: unknown,
    properties?: EventProperties[E]
  ) {
    analytics.capture(event, properties);
    const { tags, extra } = splitTags(
      properties as Record<string, unknown> | undefined
    );
    Sentry.captureException(error, { tags: { event, ...tags }, extra });
  },

  /** One identity across PostHog, Sentry and the API: the user's guid. */
  identify(userId: string, traits: { email?: string; name?: string } = {}) {
    if (typeof window === "undefined") return;
    if (posthog.__loaded) posthog.identify(userId, traits);
    Sentry.setUser({ id: userId, email: traits.email, username: traits.name });
    const distinctId = posthog.__loaded ? posthog.get_distinct_id() : undefined;
    if (distinctId) Sentry.setTag("posthog_distinct_id", distinctId);
  },

  /** A guest has no account; the device id is what the API knows them by. */
  identifyGuest(deviceId: string) {
    Sentry.setUser({ id: deviceId });
  },

  reset() {
    if (typeof window === "undefined") return;
    if (posthog.__loaded) posthog.reset();
    Sentry.setUser(null);
  },
};
